import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { HIST } from "@/constants/testIds";
import { toast } from "sonner";
import { Download, RotateCw, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LABELS = {
  female: "Female", male: "Male",
  young_adult: "Young adult", adult: "Adult", mature: "Mature",
  standing: "Standing", walking: "Walking", casual: "Casual", full_body: "Full body",
  front: "Full Body — Front", three_quarter: "Full Body — 3/4", dynamic: "Full Body — Side / Walking",
  premium: "Full Body — Editorial", medium: "Waist Up — Medium", close_up: "Close-up Detail",
  side: "Side", back: "Back", sitting: "Sitting",
  studio: "Studio", outdoor: "Outdoor", luxury_fashion: "Luxury", minimalist: "Minimalist",
};

export default function History() {
  const { refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [retrying, setRetrying] = useState({});

  const pollRef = useRef(null);

  const load = () => api.get("/history?limit=60").then((r) => setItems(r.data.items || [])).catch(() => {});

  useEffect(() => {
    load();
    return () => pollRef.current && clearTimeout(pollRef.current);
  }, []);

  const pendingCount = useMemo(() => items.filter((it) => it.status === "pending").length, [items]);
  const inProgress = useMemo(() => {
    const bySeries = {};
    items.forEach((it) => {
      if (!it.series_id) return;
      const s = (bySeries[it.series_id] ||= { series_id: it.series_id, total: 0, done: 0, pending: 0 });
      s.total += 1;
      if (it.status === "completed") s.done += 1;
      if (it.status === "pending") s.pending += 1;
    });
    return Object.values(bySeries).filter((s) => s.pending > 0);
  }, [items]);

  // Live progress: poll while any job is still rendering.
  useEffect(() => {
    if (pendingCount === 0) return undefined;
    pollRef.current = setTimeout(load, 3000);
    return () => clearTimeout(pollRef.current);
  }, [pendingCount, items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (kind === "content" && it.source !== "content_studio") return false;
      if (kind === "photo" && it.source === "content_studio") return false;
      if (!needle) return true;
      return (
        it.prompt?.toLowerCase().includes(needle) ||
        it.custom_prompt?.toLowerCase().includes(needle) ||
        it.headline?.toLowerCase().includes(needle) ||
        it.topic?.toLowerCase().includes(needle) ||
        it.caption?.toLowerCase().includes(needle) ||
        it.post_caption?.toLowerCase().includes(needle) ||
        Object.values(it.settings || {}).join(" ").toLowerCase().includes(needle)
      );
    });
  }, [items, q, kind]);

  // Group Content Studio slides so one card can copy every caption in the pack.
  const slideGroups = useMemo(() => {
    const groups = {};
    items.forEach((it) => {
      if (it.source !== "content_studio") return;
      const key = it.content_job_id || it.job_id;
      (groups[key] ||= []).push(it);
    });
    Object.values(groups).forEach((list) => list.sort((a, b) => a.job_id.localeCompare(b.job_id)));
    return groups;
  }, [items]);

  const contentCount = useMemo(
    () => items.filter((it) => it.source === "content_studio").length,
    [items],
  );

  const retry = async (jobId) => {
    setRetrying((r) => ({ ...r, [jobId]: true }));
    try {
      await api.post(`/generate/rerun/${jobId}`);
      await refresh();
      await load();
      toast.success("Regeneration complete.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Retry failed.");
    } finally {
      setRetrying((r) => ({ ...r, [jobId]: false }));
    }
  };

  const download = async (path, jobId) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `photo-${jobId}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    }
  };

  const copyText = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Archive</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-zinc-950">
            Your <span className="italic text-zinc-500">renders</span>
          </h1>
        </div>
        <input
          data-testid={HIST.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search prompts or settings…"
          className="w-full md:w-80 border border-zinc-300 focus:border-zinc-900 outline-none px-4 py-2.5 text-sm bg-white"
        />
      </div>

      {inProgress.length > 0 && (
        <div data-testid="history-progress-banner" className="mb-8 border border-zinc-900 bg-white p-5">
          {inProgress.map((s) => (
            <div key={s.series_id} data-testid={`history-progress-${s.series_id}`} className="py-1.5">
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-700">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Photoshoot in progress · {s.series_id}
                </span>
                <span className="font-mono text-[10px] tabular-nums text-zinc-500">
                  {s.done}/{s.total} ready
                </span>
              </div>
              <div className="mt-2 h-[2px] bg-zinc-100">
                <div className="h-full bg-zinc-950 transition-[width] duration-700" style={{ width: `${Math.round((s.done / s.total) * 100)}%` }} />
              </div>
            </div>
          ))}
          <p className="mt-3 text-[12px] text-zinc-500">
            You can leave this page — photos keep rendering and appear here when ready.
          </p>
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        {[
          { v: "all", label: `Semua (${items.length})` },
          { v: "content", label: `Konten Studio (${contentCount})` },
          { v: "photo", label: `Foto (${items.length - contentCount})` },
        ].map((option) => (
          <button
            key={option.v}
            type="button"
            onClick={() => setKind(option.v)}
            className={`border px-3 py-1.5 text-[12px] ${
              kind === option.v
                ? "border-zinc-950 bg-zinc-950 text-[#FBFBF9]"
                : "border-zinc-300 text-zinc-600 hover:border-zinc-900"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-zinc-300 py-24 text-center">
          <p className="text-zinc-500 text-sm">No generations yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {filtered.map((it) => (
            <div
              key={it.job_id}
              data-testid={HIST.itemCard}
              className="border border-zinc-200 bg-white p-6 grid md:grid-cols-12 gap-6"
            >
              <div className="md:col-span-2 aspect-[4/5] bg-zinc-100 overflow-hidden">
                {it.original_path ? (
                  <AuthImage width={320} path={it.original_path} alt="" className="w-full h-full object-cover" />
                ) : null}
              </div>
              <div className="md:col-span-4 aspect-[4/5] bg-zinc-100 overflow-hidden">
                {it.generated_path ? (
                  <AuthImage width={320} path={it.generated_path} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                    {it.status === "pending" && (
                      it.started_at ? <Loader2 className="h-4 w-4 animate-spin text-zinc-500" /> : <span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
                    )}
                    {it.status === "pending" ? (it.started_at ? "rendering" : "queued") : it.status}
                  </div>
                )}
              </div>
              <div className="md:col-span-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-block h-1.5 w-1.5 rounded-full ${
                        it.status === "completed"
                          ? "bg-emerald-500"
                          : it.status === "failed"
                          ? "bg-rose-500"
                          : "bg-amber-500"
                      }`}
                    />
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {it.status}
                    </span>
                    <span className="font-mono text-[10px] text-zinc-400">·</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                      {new Date(it.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-3 font-serif text-lg text-zinc-950">
                    {LABELS[it.settings?.gender]} · {LABELS[it.settings?.age]} · {it.pose_label || LABELS[it.settings?.pose] || it.settings?.pose}
                  </p>
                  <p className="mt-1 text-[13px] text-zinc-500">
                    {it.source === "content_studio" ? (
                      <>
                        {it.pose_label}
                        {it.topic ? ` · ${it.topic}` : ""}
                      </>
                    ) : (
                      <>
                        Background: {LABELS[it.settings?.background]} · Aspect{" "}
                        {it.settings?.aspect_ratio}
                      </>
                    )}
                  </p>
                  {it.source === "content_studio" && (
                    <div className="mt-3 space-y-2">
                      {it.headline && (
                        <p className="text-[13px] font-medium text-zinc-900">
                          {it.headline}
                        </p>
                      )}
                      {it.caption && (
                        <p className="whitespace-pre-line text-[12px] leading-relaxed text-zinc-600">
                          {it.caption}
                        </p>
                      )}
                      {it.post_caption && (
                        <details open className="border border-zinc-200 bg-zinc-50 px-3 py-2">
                          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                            Caption utama + hashtag
                          </summary>
                          <p className="mt-2 whitespace-pre-line text-[12px] leading-relaxed text-zinc-700">
                            {it.post_caption}
                          </p>
                          {Array.isArray(it.hashtags) && it.hashtags.length > 0 && (
                            <p className="mt-2 text-[12px] text-zinc-600">
                              {it.hashtags.join(" ")}
                            </p>
                          )}
                          {it.best_time && (
                            <p className="mt-2 text-[11px] text-zinc-500">
                              Best time: {it.best_time}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                copyText(
                                  `${it.post_caption}\n\n${(it.hashtags || []).join(" ")}`.trim(),
                                  "Post caption",
                                )
                              }
                              className="border border-zinc-900 px-3 py-1.5 text-[12px] hover:bg-zinc-950 hover:text-[#FBFBF9]"
                            >
                              Copy caption utama
                            </button>
                            <button
                              type="button"
                              onClick={() => copyText(it.caption, "Slide caption")}
                              className="border border-zinc-300 px-3 py-1.5 text-[12px] hover:border-zinc-900"
                            >
                              Copy caption slide
                            </button>
                            {(() => {
                              const siblings = slideGroups[it.content_job_id || it.job_id] || [];
                              if (siblings.length < 2) return null;
                              return (
                                <button
                                  type="button"
                                  onClick={() =>
                                    copyText(
                                      siblings
                                        .map(
                                          (slide, index) =>
                                            `Slide ${index + 1}: ${(slide.caption || "").trim()}`,
                                        )
                                        .join("\n\n"),
                                      "All slide captions",
                                    )
                                  }
                                  className="border border-zinc-900 px-3 py-1.5 text-[12px] hover:bg-zinc-950 hover:text-[#FBFBF9]"
                                >
                                  Copy semua caption ({siblings.length} slide)
                                </button>
                              );
                            })()}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                  {it.shot_note && (
                    <p data-testid="history-shot-note" className="mt-3 text-[12px] text-zinc-600">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 mr-2">Shot note</span>
                      {it.shot_note}
                    </p>
                  )}
                  {it.custom_prompt && (
                    <p className="mt-4 text-[13px] italic text-zinc-700 border-l-2 border-zinc-900 pl-3">
                      “{it.custom_prompt}”
                    </p>
                  )}
                  {it.error?.message && (
                    <p className="mt-3 text-[12px] text-rose-600">{it.error.message}</p>
                  )}
                </div>
                {it.generated_path && (
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={HIST.download}
                      onClick={() => download(it.generated_path, it.job_id)}
                      className="mt-4 inline-flex items-center gap-2 border border-zinc-900 px-4 py-2 text-sm hover:bg-zinc-950 hover:text-[#FBFBF9] transition-colors"
                    >
                      <Download className="h-4 w-4" /> Download
                    </button>
                  </div>
                )}
                {(it.status === "failed" || it.status === "skipped") && (
                  <button
                    data-testid={HIST.retry}
                    disabled={!!retrying[it.job_id]}
                    onClick={() => retry(it.job_id)}
                    className="mt-4 inline-flex items-center gap-2 bg-zinc-950 text-[#FBFBF9] px-4 py-2 text-sm hover:bg-zinc-800 disabled:bg-zinc-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <RotateCw className={`h-4 w-4 ${retrying[it.job_id] ? "animate-spin" : ""}`} />
                    {retrying[it.job_id] ? "Retrying…" : "Try again"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
